import type { EnneagramTypeId } from "@/types/supabase";
import type { ClassifyResult, ExpressAnswers } from "./types";
import { SIGNALS } from "./signals";

const ALL_TYPES: readonly EnneagramTypeId[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

function emptyBreakdown(): Record<EnneagramTypeId, number> {
  return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
}

/**
 * Pure deterministic classifier. Each answered signal contributes 1 vote to the
 * mapped type. `frase` (assinatura linguística literal) carries weight 2 — it
 * is the strongest signal per Rafa Medeiros method (linha 309 do pillar).
 *
 * Confidence = primaryVotes / totalAnswered.
 * Ambiguous = primary leads secondary by less than 2 votes OR <3 signals answered.
 */
export function classify(answers: ExpressAnswers): ClassifyResult {
  const breakdown = emptyBreakdown();
  let totalWeight = 0;

  for (const signal of SIGNALS) {
    const choice = answers[signal.id];
    if (choice == null) continue;
    const weight = signal.id === "frase" ? 2 : 1;
    breakdown[choice] += weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    throw new Error("classify: at least one signal must be answered.");
  }

  const ranked = [...ALL_TYPES].sort((a, b) => breakdown[b] - breakdown[a]);
  const primary = ranked[0];
  const primaryVotes = breakdown[primary];
  const secondaryCandidate = ranked[1];
  const secondaryVotes = breakdown[secondaryCandidate];

  const secondary = secondaryVotes > 0 ? secondaryCandidate : null;
  const lead = primaryVotes - secondaryVotes;
  const answeredCount = Object.values(answers).filter((v) => v != null).length;
  const ambiguous = lead < 2 || answeredCount < 3;
  const confidence = totalWeight === 0 ? 0 : primaryVotes / totalWeight;

  return {
    primary,
    secondary,
    confidence,
    breakdown,
    ambiguous,
  };
}

export function answeredSignalCount(answers: ExpressAnswers): number {
  return Object.values(answers).filter((v) => v != null).length;
}
